import {
	Box,
	createStyles,
	Divider,
	Group,
	ScrollArea,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { EntityType } from '@repo/common/enums/entityType';
import { Button, Text, Title } from '@repo/foundations';
import { useMount } from 'ahooks';
import dayjs from 'dayjs';
import { sortBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import type { IIntegration, IWorkspace } from '../../../api';
import {
	currentWorkspaceQueryKey,
	integrationsQueryKeyFactory,
	queryClient,
	updateWorkspace,
	useAuthUser,
	useSuspenseIntegrationList,
} from '../../../api';
import { AuthCard } from '../../../components/Auth/AuthCard';
import { AuthFooter } from '../../../components/Auth/AuthLayouts/AuthFooter';
import { AuthLayoutBase } from '../../../components/Auth/AuthLayouts/AuthLayoutBase';
import IntegrationLogo from '../../../components/IntegrationLogo';
import { IntegrationStatusRender } from '../../../components/TableV2/render';

const useStyles = createStyles((theme) => ({
	wrapper: {
		background: theme.other.getColor('surface/primary/default'),
		borderRadius: theme.radius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
	},
}));

export function OnboardingIntegrationPage() {
	const { classes } = useStyles();
	const { workspace } = useAuthUser();
	const { data: integrations, isLoading } = useSuspenseIntegrationList({
		options: {
			refetchInterval: 10_000, // 10 seconds
			refetchOnWindowFocus: true,
			refetchOnMount: true,
		},
	});

	const navigate = useNavigate();

	useMount(() => {
		if (!isLoading && integrations?.results?.length === 0) {
			navigate('/onboarding/welcome_lite?skip_welcome=true');
		}
	});

	const handleContinue = useCallback(async () => {
		await updateWorkspace({
			data: {
				id: workspace?.id,
				integration_onboarding_completed: true,
			} as IWorkspace,
		});

		queryClient.invalidateQueries({
			queryKey: currentWorkspaceQueryKey,
		});
		navigate('/');
	}, [workspace?.id, navigate]);

	const hasBegunIntegrationConnection = useMemo(() => {
		const hasAnyIntegration = (integrations?.results?.length ?? 0) > 0;
		return hasAnyIntegration || workspace?.integration_onboarding_completed;
	}, [integrations?.results, workspace?.integration_onboarding_completed]);

	const sortedIntegrations = useMemo(
		() =>
			sortBy(integrations?.results, (integration) =>
				dayjs(integration.created_at).unix()
			).reverse(),
		[integrations]
	);

	const handleIntegrationClick = useCallback(
		(integration: IIntegration) => {
			queryClient.invalidateQueries({
				queryKey: integrationsQueryKeyFactory.all(),
			});
			navigate(
				`/onboarding/welcome_lite?type=${integration.type}&integration_id=${integration.id}`
			);
		},
		[navigate]
	);

	return (
		<AuthLayoutBase>
			<AuthCard width={800} p="2xl">
				<Stack spacing="xl">
					<Stack spacing="3xs">
						<Title>Connecting to your data</Title>
						<Text size="md" color="text/secondary/default">
							We’ll let you know when your data has successfully synced. It’s
							normal for your first sync to take a little longer, hang tight
							while we get your workspace ready
						</Text>
					</Stack>
					<ScrollArea.Autosize mah={428}>
						<Stack spacing={0} className={classes.wrapper}>
							{sortedIntegrations?.map((integration, index) => {
								const spec = integrationList.find(
									(item) => item.type === integration.type
								);

								// Display "pending" statuses as "running" during onboarding
								const displayStatus =
									integration.status === 'pending'
										? 'running'
										: integration.status;

								return (
									<UnstyledButton
										onClick={() => handleIntegrationClick(integration)}
										key={integration.id}
									>
										<Group noWrap spacing="sm" py="sm" px="md">
											<Group noWrap spacing="sm" style={{ flex: 1 }}>
												<IntegrationLogo
													size={32}
													entityType={EntityType.integration}
													integrationType={integration.type}
												/>
												<Stack spacing={0}>
													<Text size="sm" weight="bold">
														{integration.name || spec?.name}
													</Text>
													<Text size="xs" color="text/secondary/default">
														{spec?.description}
													</Text>
												</Stack>
											</Group>
											<IntegrationStatusRender
												record={{ ...integration, status: displayStatus }}
												animate={displayStatus === 'running'}
											/>
										</Group>
										{index !== integrations?.results?.length - 1 && (
											<Box px="sm">
												<Divider size="xs" />
											</Box>
										)}
									</UnstyledButton>
								);
							})}
						</Stack>
					</ScrollArea.Autosize>
				</Stack>
				<AuthFooter mt="2xl">
					<Group position="right" py="md" px="2xl">
						<Group spacing="xs" position="right">
							{hasBegunIntegrationConnection ? (
								<>
									<Button
										component={Link}
										to="/onboarding/welcome_lite?skip_welcome=true"
										variant="default"
									>
										Add new integration
									</Button>
									<Button onClick={handleContinue} variant="primary">
										Continue
									</Button>
								</>
							) : (
								<Button
									component={Link}
									to="/onboarding/welcome_lite?skip_welcome=true"
									variant="primary"
								>
									Add new integration
								</Button>
							)}
						</Group>
					</Group>
				</AuthFooter>
			</AuthCard>
		</AuthLayoutBase>
	);
}
